import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { ReflectionScreen } from "@/components/sim/ReflectionScreen";
import { ScreenShell, GlassCard } from "@/components/sim/shared";
import { useAttemptStore } from "@/store/attemptStore";

/**
 * ReflectionPage — final reflection screen before AI coaching.
 * Uses the ported NeuArena ReflectionScreen (sidebar quote cards + prompt
 * + optional skip). NeuSim flow preserved: saveReflection() then navigate
 * to /coaching. Skip is offered only when the sim hasn't marked reflection
 * as required (ReflectionScreenRequired === "Yes").
 */
export function ReflectionPage(): JSX.Element {
  const { simId, attemptId } = useParams<{ simId: string; attemptId: string }>();
  const navigate = useNavigate();
  const { tree, attempt, saveReflection } = useAttemptStore();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const required = tree?.settings["ReflectionScreenRequired"] === "Yes";
  const minChars = Math.max(0, Number(tree?.settings["ReflectionMinChars"] ?? 0) || 0);

  const handleSave = async (text: string): Promise<void> => {
    if (attemptId === undefined || simId === undefined) return;
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await saveReflection(attemptId, text);
      navigate(`/sim/${simId}/attempt/${attemptId}/coaching`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your reflection");
      setSaving(false);
    }
  };

  const handleSkip = (): void => {
    if (simId === undefined || attemptId === undefined) return;
    navigate(`/sim/${simId}/attempt/${attemptId}/coaching`);
  };

  return (
    <>
      <ReflectionScreen
        question="What will you do differently after today?"
        xp={attempt?.totalXp ?? 0}
        minChars={minChars}
        onContinue={(text) => void handleSave(text)}
        onSkip={!required ? handleSkip : undefined}
      />
      {error !== null && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <GlassCard className="px-5 py-3">
            <div className="flex items-center gap-2 text-sm text-[#f87272]">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          </GlassCard>
        </div>
      )}
    </>
  );
}
